import { CobrowseController, type ControlRequestHandler } from '@/cobrowse/CobrowseController';
import { resolveConsentState } from '@/consent';
import { httpStatusOf, RecorderError, RecorderErrorCode } from '@/errors';
import { EventQueue } from '@/events/EventQueue';
import { createLogger, setLogLevel, LogLevel } from '@/logger';
import { startRouteTracker, stopRouteTracker } from '@/recording/RouteTracker';
import { RrwebDomRecorder } from '@/recording/RrwebDomRecorder';
import { StylesheetInliner } from '@/recording/StylesheetInliner';
import { SessionClient } from '@/session/SessionClient';
import { SessionStorageEventStore } from '@/storage/SessionStorageEventStore';
import { BeaconTransport } from '@/transport/BeaconTransport';
import { createLiveClient } from '@/transport/live/createLiveClient';
import { RestTransport } from '@/transport/RestTransport';
import { TransportChain } from '@/transport/TransportChain';
import type {
  IDomRecorder,
  IEventStore,
  ISessionClient,
  ITransport,
  RecorderConfig,
  RecorderState,
  RecorderStatus,
} from '@/types';

const SESSION_KEY = 'cobrowsing_activeSessionId';
// Anon visitor id, persisted across tabs/visits in localStorage. Used as the
// recorder's `Session.userId` when no caller-supplied id is available, so the
// per-agent watchlist can follow a visitor across pageloads.
const VISITOR_ID_KEY = 'cobrowsing_visitorId';
const VISITOR_ID_PREFIX = 'cnv_';
// Mirrors the backend's `Session.userId` VARCHAR(191) column cap, so we fail
// fast on the client instead of round-tripping a 400 from the API.
const MAX_VISITOR_ID_LEN = 191;
const SESSION_RETRY_ATTEMPTS = 3;
const SESSION_RETRY_BASE_DELAY_MS = 1_000;

const log = createLogger('Recorder');

// ---------------------------------------------------------------------------
// Recorder
// ---------------------------------------------------------------------------

/**
 * Orchestrates session creation, DOM recording, event buffering, and
 * transport — but owns none of that logic itself.
 *
 * Dependency-Inversion: every collaborator is injected via the constructor
 * (or built through the static factory), so each can be replaced or mocked
 * independently in tests.
 *
 * @example
 * ```typescript
 * import { Recorder } from 'cobrowsing-ui';
 *
 * const recorder = Recorder.create({
 *   apiUrl: 'https://api.chatneer.com',
 *   apiKey: 'cnr_xxx'
 * });
 *
 * const sessionId = await recorder.startRecording();
 * console.log('Recording:', sessionId);
 *
 * // Later...
 * await recorder.stopRecording();
 * ```
 */
export class Recorder {
  private state: RecorderState = 'idle';
  private sessionId = '';
  private stopping = false;
  private _transport: ITransport | null = null;
  private _queue: EventQueue | null = null;
  private _liveClient: import('@/transport/live/LiveClient').LiveClient | null = null;
  private _cursorCallback: ((x: number, y: number) => void) | null = null;
  private _stylesheetInliner: StylesheetInliner | null = null;
  private _ghostCursor: HTMLElement | null = null;
  private _cursorHideTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly _CURSOR_HIDE_DELAY_MS = 2_000;

  /**
   * Cobrowse consent + presence controller. Owns the visitor-side chip,
   * the consent modal, and the `isControlGranted()` gate that
   * `_handleCursorMessage` / `_handleClickMessage` consult.
   */
  private readonly _cobrowse = new CobrowseController({
    onVisitorEndLiveView: () => {
      // Visitor hit End — clear local cobrowse state (chip already hidden,
      // grant cleared by CobrowseController). We intentionally do NOT close
      // the live transport: keeping the recorder WS alive means the session
      // stays in the admin presence list so an agent can re-engage if
      // needed. The `liveview:end` message already flowed upstream and the
      // server has revoked any active grant.
    },
  });

  /**
   * Per-instance fallback when `localStorage` is unavailable (private browsing,
   * storage quota, partitioned-storage edge cases). Lasts only as long as this
   * Recorder instance, so the visitor id is stable within a single tab/session
   * but lost across reloads — same caveat the user gets in incognito anyway.
   */
  private _ephemeralVisitorId: string | null = null;

  /**
   * Once-per-Recorder dev-mode hint when no caller-supplied `userId` is
   * available. Without `setVisitorId()` integration, `Session.userId` will be
   * the anon `cnv_*` id — which won't match the dashboard's sb_users-keyed
   * watchlist. Warning fires once to nudge the integrator without spamming.
   */
  private _warnedAnonVisitorId = false;

  private constructor(
    // Mutable so `setVisitorId()` can swap the effective `userId` at runtime
    // without rebuilding the recorder. Other fields stay readonly.
    private config: RecorderConfig,
    private readonly sessionClient: ISessionClient,
    private readonly domRecorder: IDomRecorder,
    private readonly transportFactory: (sessionId: string) => Promise<ITransport>,
    private readonly store: IEventStore
  ) {}

  // -------------------------------------------------------------------------
  // Static factory — wires up the real dependency graph
  // -------------------------------------------------------------------------

  /**
   * Creates a new Recorder instance with the given configuration.
   *
   * @param config - Configuration options
   * @returns Configured Recorder instance (not yet started)
   * @throws Error if apiUrl or apiKey is missing, or apiUrl is invalid
   *
   * @example
   * ```typescript
   * const recorder = Recorder.create({
   *   apiUrl: 'https://api.chatneer.com',
   *   apiKey: 'cnr_xxx',
   *   flushIntervalMs: 1000,  // optional: flush every 1s
   *   maskInputOptions: {     // optional: customize input masking
   *     password: true,
   *     tel: true
   *   }
   * });
   * ```
   */
  static create(config: RecorderConfig): Recorder {
    if (!config.apiUrl) throw new RecorderError(RecorderErrorCode.MISSING_API_URL);
    if (!config.apiKey) throw new RecorderError(RecorderErrorCode.MISSING_API_KEY);

    try {
      new URL(config.apiUrl);
    } catch {
      throw new RecorderError(RecorderErrorCode.INVALID_API_URL);
    }

    if (config.flushIntervalMs !== undefined && config.flushIntervalMs <= 0)
      throw new RecorderError(RecorderErrorCode.INVALID_FLUSH_INTERVAL);
    if (config.mousemoveSampleMs !== undefined && config.mousemoveSampleMs <= 0)
      throw new RecorderError(RecorderErrorCode.INVALID_SAMPLE_INTERVAL, undefined, {
        field: 'mousemoveSampleMs',
      });
    if (config.scrollSampleMs !== undefined && config.scrollSampleMs <= 0)
      throw new RecorderError(RecorderErrorCode.INVALID_SAMPLE_INTERVAL, undefined, {
        field: 'scrollSampleMs',
      });
    if (config.checkoutEveryNms !== undefined && config.checkoutEveryNms <= 0)
      throw new RecorderError(RecorderErrorCode.INVALID_CHECKOUT_INTERVAL);
    if (config.wsOpenTimeoutMs !== undefined && config.wsOpenTimeoutMs <= 0)
      throw new RecorderError(RecorderErrorCode.INVALID_WS_TIMEOUT);
    if (config.wsReconnectAttempts !== undefined && config.wsReconnectAttempts < 0)
      throw new RecorderError(RecorderErrorCode.INVALID_WS_RECONNECT_ATTEMPTS);
    if (config.wsReconnectBaseDelayMs !== undefined && config.wsReconnectBaseDelayMs <= 0)
      throw new RecorderError(RecorderErrorCode.INVALID_WS_RECONNECT_DELAY);

    // Configure logging based on debug flag
    if (config.debug) {
      setLogLevel(LogLevel.DEBUG);
    }

    const sessionClient = new SessionClient(config.apiUrl, config.apiKey, config.csrfToken);
    const domRecorder = new RrwebDomRecorder(config);
    const store = new SessionStorageEventStore();

    const recorder = new Recorder(
      config,
      sessionClient,
      domRecorder,
      async (sessionId: string): Promise<ITransport> => {
        const rest = new RestTransport(config.apiUrl, config.apiKey, sessionId, config.csrfToken);

        // Transport is whatever the backend advertised when this session was
        // created or resumed (see SessionClient.liveDescriptor). Falls back to
        // `ws` only when talking to an older backend that doesn't report one.
        const liveProvider = sessionClient.liveDescriptor?.provider ?? 'ws';

        // Pusher is server→client only. The backend broadcasts to viewers via
        // Pusher when it receives events over REST, so the recorder uses REST
        // exclusively — no live WS connection is opened.
        if (liveProvider === 'pusher') {
          log.debug('Pusher mode: recorder using REST-only transport');
          return rest;
        }

        const live = createLiveClient(liveProvider, {
          apiUrl: config.apiUrl,
          apiKey: config.apiKey,
          sessionId,
          openTimeoutMs: config.wsOpenTimeoutMs,
          reconnectAttempts: config.wsReconnectAttempts,
          reconnectBaseDelayMs: config.wsReconnectBaseDelayMs,
        });

        try {
          await live.connect();
        } catch {
          log.warn(`Live transport (${liveProvider}) unavailable, using REST fallback`);
        }

        // Cursor moves the ghost overlay; click dispatches a real click;
        // focus moves the caret into a focusable element.
        live.onCursorMessage(recorder._handleCursorMessage);
        live.onClickMessage(recorder._handleClickMessage);
        live.onFocusMessage(recorder._handleFocusMessage);
        // Route viewer presence + consent messages through the controller.
        live.onMessage((msg) => recorder._cobrowse.handleServerMessage(msg));
        recorder._cobrowse.setSend((msg) => live.sendRaw(msg));
        recorder._liveClient = live;

        // Live first, REST as fallback — TransportChain picks the first ready one.
        return new TransportChain([live, rest]);
      },
      store
    );
    return recorder;
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Starts a fresh recording session — always creates a new session on the
   * backend, then begins capture and the flush loop (WebSocket, falling back
   * to REST). For the consent-aware flow that reuses an existing session from
   * this tab, use {@link startRecording}.
   *
   * @returns the new session ID
   * @throws RecorderError ALREADY_STOPPED if this instance was already stopped
   * @throws RecorderError CONSENT_DENIED if the user has opted out
   */
  async start(): Promise<string> {
    return this.begin({ reuseSession: false });
  }

  /**
   * Starts recording, honouring stored consent and reusing a still-valid
   * session from this tab's storage if one exists (otherwise creates a new
   * one). Use this for support / opt-in flows.
   *
   * @returns the active session ID
   * @throws RecorderError ALREADY_STOPPED if this instance was already stopped
   * @throws RecorderError CONSENT_DENIED if the user has opted out
   */
  async startRecording(): Promise<string> {
    return this.begin({ reuseSession: true });
  }

  /**
   * Stops recording and releases all resources (stops capture, flushes
   * buffered events, disposes transports). Safe to call multiple times.
   * The session id is kept so {@link getStatus} still reports it.
   */
  async stop(): Promise<void> {
    return this.end(false);
  }

  /**
   * Stops recording and clears the session from storage, so the next
   * {@link startRecording} starts a clean session. No-op unless recording.
   */
  async stopRecording(): Promise<void> {
    if (this.state !== 'recording') return;
    return this.end(true);
  }

  // -------------------------------------------------------------------------
  // Private helper methods
  // -------------------------------------------------------------------------

  /** Shared start path for {@link start} and {@link startRecording}. */
  private async begin({ reuseSession }: { reuseSession: boolean }): Promise<string> {
    if (this.state === 'recording') return this.sessionId;
    if (this.state === 'stopped') {
      throw new RecorderError(
        RecorderErrorCode.ALREADY_STOPPED,
        'Recorder has been stopped and cannot be restarted. Create a new instance.'
      );
    }
    if (this.getConsentState() === 'denied') {
      throw new RecorderError(RecorderErrorCode.CONSENT_DENIED);
    }

    try {
      let sessionId = reuseSession ? await this.tryReuseSession() : '';
      if (!sessionId) sessionId = await this.createNewSession();
      this.sessionId = sessionId;
      await this._wire(sessionId);
      return sessionId;
    } catch (err) {
      this.state = 'error';
      log.error('Failed to start recording', err);
      throw err;
    }
  }

  /** Shared stop path for {@link stop} and {@link stopRecording}. */
  private async end(clearSessionId: boolean): Promise<void> {
    if (this.stopping || this.state === 'stopped') return;
    this.stopping = true;
    try {
      await this._teardown(clearSessionId);
    } finally {
      this.stopping = false;
    }
  }

  /** Returns a still-valid session id from this tab's storage, or '' if none. */
  private async tryReuseSession(): Promise<string> {
    const existing = this.getExistingSessionId();
    if (!existing) return '';
    if (!(await this.validateSession(existing))) return '';
    log.debug('Reusing existing session', existing);
    return existing;
  }

  private _injectGhostCursor(): void {
    if (this._ghostCursor || typeof document === 'undefined') return;
    const el = document.createElement('div');
    el.setAttribute('data-cobrowsing-cursor', '');
    el.style.cssText =
      'position:fixed;width:20px;height:20px;border-radius:50%;' +
      'background:rgba(99,102,241,0.7);border:2px solid #6366f1;' +
      'pointer-events:none;left:0;top:0;display:none;z-index:2147483647;box-sizing:border-box';
    document.body.appendChild(el);
    this._ghostCursor = el;
  }

  private _removeGhostCursor(): void {
    if (this._cursorHideTimer !== null) {
      clearTimeout(this._cursorHideTimer);
      this._cursorHideTimer = null;
    }
    this._ghostCursor?.remove();
    this._ghostCursor = null;
  }

  // Arrow functions so they can be passed as callbacks without losing `this`.
  private _handleCursorMessage = (nx: number, ny: number): void => {
    if (typeof window === 'undefined') return;
    // Consent gate: drop until the visitor grants control. The server
    // enforces this too — belt-and-braces.
    if (!this._cobrowse.isControlGranted()) return;
    const px = nx * document.documentElement.clientWidth;
    const py = ny * document.documentElement.clientHeight;
    if (this._ghostCursor) {
      this._ghostCursor.style.display = 'block';
      this._ghostCursor.style.transform = `translate(calc(${px}px - 50%), calc(${py}px - 50%))`;
    }
    // Hide ghost cursor if no message arrives within the delay (agent left player).
    if (this._cursorHideTimer !== null) clearTimeout(this._cursorHideTimer);
    this._cursorHideTimer = setTimeout(() => {
      this._cursorHideTimer = null;
      if (this._ghostCursor) this._ghostCursor.style.display = 'none';
    }, this._CURSOR_HIDE_DELAY_MS);
    this._cursorCallback?.(px, py);
  };

  private _handleClickMessage = (nx: number, ny: number): void => {
    // Consent gate first — even with `allowAgentClicks: true`, a click must
    // not reach the DOM until the visitor has granted control.
    if (!this._cobrowse.isControlGranted()) return;
    // Dispatching a real click is indistinguishable from a user click to
    // form handlers / nav, so it's off by default; opt in via config.
    if (this.config.allowAgentClicks !== true) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const px = nx * document.documentElement.clientWidth;
    const py = ny * document.documentElement.clientHeight;
    const el = document.elementFromPoint(px, py);
    if (!el) return;
    el.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: px,
        clientY: py,
      })
    );
  };

  private _handleFocusMessage = (nx: number, ny: number): void => {
    // Consent-gated like cursor/click. Focus only moves the caret — it can't
    // submit, navigate, or read anything — so it isn't behind `allowAgentClicks`.
    if (!this._cobrowse.isControlGranted()) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const px = nx * document.documentElement.clientWidth;
    const py = ny * document.documentElement.clientHeight;
    const el = document.elementFromPoint(px, py);
    if (el instanceof HTMLElement && Recorder._isFocusable(el)) {
      el.focus();
    }
  };

  /** Naturally focusable elements + anything explicitly made focusable. */
  private static _isFocusable(el: HTMLElement): boolean {
    if (el.isContentEditable) return true;
    if (el.tabIndex >= 0) return true;
    switch (el.tagName) {
      case 'INPUT':
      case 'TEXTAREA':
      case 'SELECT':
      case 'BUTTON':
        return true;
      case 'A':
        return (el as HTMLAnchorElement).hasAttribute('href');
      default:
        return false;
    }
  }

  private async _wire(sessionId: string): Promise<void> {
    const transport = await this.transportFactory(sessionId);
    // Ghost cursor is only useful when the live channel can carry cursor
    // updates — which is the WS path. Pusher / REST-only modes never receive
    // cursor data, so skip the DOM injection.
    if (this._liveClient) this._injectGhostCursor();
    const beacon = new BeaconTransport(
      this.config.apiUrl,
      this.config.apiKey,
      sessionId,
      this.config.csrfToken
    );
    const queue = new EventQueue(
      transport,
      this.store,
      this.config.flushIntervalMs,
      beacon,
      () => {
        void this.stop();
      },
      this.config.maxBufferSizeBytes
    );

    // Inline cross-origin stylesheets rrweb can't read (CORS) *before* the first
    // snapshot, so replay is self-contained — otherwise cross-origin-styled UI
    // (e.g. a third-party chat widget) renders unstyled when its origin isn't
    // reachable at replay time. Best-effort: never let it block recording.
    if (this.config.inlineCrossOriginCss !== false && typeof document !== 'undefined') {
      this._stylesheetInliner = new StylesheetInliner({
        apiUrl: this.config.apiUrl,
        apiKey: this.config.apiKey,
      });
      try {
        await this._stylesheetInliner.start();
      } catch (err) {
        log.warn('stylesheet inlining failed to start', err);
      }
    }

    this.domRecorder.start((event) => queue.push(event));
    queue.start();

    // Auto-instrument the History API so SPA route changes (React Router /
    // Vue Router / pushState / popstate / BFCache restore) emit a
    // `route-change` custom event into the rrweb stream. The dashboard
    // replayer's Pages tab consumes this event shape directly. Multipage
    // sites are already covered by rrweb's fresh Meta event on each
    // full-page load, so this is a no-op for them.
    startRouteTracker((payload) => {
      try {
        this.domRecorder.addCustomEvent('route-change', payload);
      } catch (err) {
        log.warn('route-change custom event failed', err);
      }
    });

    this._transport = transport;
    this._queue = queue;
    this.state = 'recording';
  }

  private async _teardown(clearSessionId: boolean): Promise<void> {
    this.domRecorder.stop();
    stopRouteTracker();
    this._stylesheetInliner?.stop();
    this._stylesheetInliner = null;
    if (this._queue) await this._queue.drain();
    if (this._transport) this._transport.dispose();
    this._removeGhostCursor();
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // Private browsing mode - ignore
    }
    if (clearSessionId) this.sessionId = '';
    this.state = 'stopped';
  }

  /**
   * Resolves the current consent state. See `consent.ts#resolveConsentState`
   * for the full priority chain. Lives in a shared module so the public
   * `getConsent()` export reports the same state the recorder will gate on.
   */
  private getConsentState(): 'pending' | 'granted' | 'denied' {
    return resolveConsentState({ autoGrantConsent: this.config.autoGrantConsent });
  }

  /**
   * Returns the stable visitor id used as `Session.userId`. Resolution order:
   *   1. Override set via {@link setVisitorId} (or a fresh init().userId).
   *   2. Anything we've already persisted to `localStorage` (anon or supplied).
   *   3. In-memory fallback (when localStorage is unavailable).
   *   4. Newly generated `cnv_<uuid>`, persisted to localStorage if possible.
   *
   * Caller-supplied ids are written to localStorage on first read so the
   * next page load keeps the same id without the host page having to push
   * it again. This makes the identity handoff a one-time integration cost,
   * not a per-page concern.
   */
  private getOrCreateVisitorId(): string {
    // 1 + 2: caller-supplied id (RecorderConfig.userId) wins. Persist it on
    // first sight so subsequent reloads carry it through.
    const supplied = this.config.userId;
    if (supplied && supplied.length > 0) {
      this._tryPersistVisitorId(supplied);
      return supplied;
    }

    // From here we're on the anon `cnv_*` path. The dashboard's watchlist
    // keys off `Session.userId`, which for identified visitors should match
    // the host page's user id — so log once if we end up anon, as a hint to
    // wire `setVisitorId()` from whichever component knows the visitor.
    if (!this._warnedAnonVisitorId) {
      this._warnedAnonVisitorId = true;
      log.warn(
        'No visitor id supplied via RecorderConfig.userId or setVisitorId(); ' +
          'using anon `cnv_*` id. Sessions for known visitors will not match ' +
          'the dashboard watchlist until the host page bridges the identity.'
      );
    }

    if (this._ephemeralVisitorId) return this._ephemeralVisitorId;

    try {
      const stored = localStorage.getItem(VISITOR_ID_KEY);
      if (stored && stored.length > 0) return stored;
    } catch {
      // localStorage may throw in privacy modes; fall through to generate.
    }

    const fresh = VISITOR_ID_PREFIX + this.opaqueId();
    if (!this._tryPersistVisitorId(fresh)) {
      // Persistence failed — keep the id in memory for the life of this
      // Recorder so it's at least stable within a single tab.
      this._ephemeralVisitorId = fresh;
    }
    return fresh;
  }

  /**
   * Replaces the recorder's effective visitor id at runtime. The supplied id
   * is used as `Session.userId` on every *subsequent* session create — the
   * currently-running session (if any) keeps its previous id and is not
   * retroactively updated.
   *
   * Typical use: a host page that loads the recorder before the visitor has
   * identified (anon `cnv_*` is used). Once the chat widget reveals the
   * visitor's sb_users id, the host page calls this to bridge the recorder's
   * future sessions onto the chat-side identifier.
   *
   * Passing an empty string clears the override and any persisted id — the
   * next session create regenerates a fresh anon `cnv_<uuid>`.
   */
  setVisitorId(userId: string | null | undefined): void {
    const trimmed = typeof userId === 'string' ? userId.trim() : '';
    if (!trimmed) {
      // Reset path — drop everything so the next session creates a fresh anon.
      // Use destructure-and-omit so `userId` is *absent* (not `undefined`) on
      // the new config object; required by tsconfig's exactOptionalPropertyTypes.
      const next = { ...this.config };
      delete next.userId;
      this.config = next;
      this._ephemeralVisitorId = null;
      try {
        localStorage.removeItem(VISITOR_ID_KEY);
      } catch {
        // localStorage unavailable — nothing to clear.
      }
      return;
    }
    // Server caps `Session.userId` at 191 chars (matches the index-friendly
    // VARCHAR(191) on the column). Fail fast on the client instead of
    // round-tripping a 400 — and skip the assignment so the next session
    // create falls back to the previously-set or anon id.
    if (trimmed.length > MAX_VISITOR_ID_LEN) {
      log.warn(
        `setVisitorId: id length ${trimmed.length} exceeds ${MAX_VISITOR_ID_LEN}-char limit; ignored.`
      );
      return;
    }
    this.config = { ...this.config, userId: trimmed };
    this._ephemeralVisitorId = null;
    this._tryPersistVisitorId(trimmed);
  }

  /** True if the value made it to localStorage; false if storage refused. */
  private _tryPersistVisitorId(value: string): boolean {
    try {
      localStorage.setItem(VISITOR_ID_KEY, value);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Opaque identifier for the anonymous visitor id only. NOT a CSPRNG and
   * MUST NOT be used for tokens, secrets, or anything authentication-related —
   * the `Math.random` fallback exists for older browsers / test runners where
   * `crypto.randomUUID` is missing.
   */
  private opaqueId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private getExistingSessionId(): string | null {
    try {
      return sessionStorage.getItem(SESSION_KEY);
    } catch {
      return null;
    }
  }

  private async validateSession(sessionId: string): Promise<boolean> {
    try {
      await this.sessionClient.getSession(sessionId);
      return true;
    } catch (err) {
      if (httpStatusOf(err) === 404) return false;
      throw err;
    }
  }

  private async createNewSession(): Promise<string> {
    let lastErr: unknown;
    let newSessionId = '';

    // Visitor id: resolve once per session-create call (not per retry) so a
    // freshly-generated id is consistent across the attempt window.
    const userId = this.getOrCreateVisitorId();

    for (let attempt = 1; attempt <= SESSION_RETRY_ATTEMPTS; attempt++) {
      try {
        newSessionId = await this.sessionClient.createSession({
          url: typeof window !== 'undefined' ? window.location.href : '',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          userId,
        });
        break;
      } catch (err) {
        lastErr = err;
        if (attempt < SESSION_RETRY_ATTEMPTS) {
          await new Promise<void>((r) => setTimeout(r, SESSION_RETRY_BASE_DELAY_MS * attempt));
        }
      }
    }

    if (!newSessionId) throw lastErr;

    try {
      sessionStorage.setItem(SESSION_KEY, newSessionId);
    } catch {
      // Private browsing mode - continue without session persistence
    }

    return newSessionId;
  }

  /**
   * Adds a custom event to the session recording.
   *
   * Custom events are useful for tracking user actions that aren't
   * automatically captured by rrweb, such as button clicks, form
   * submissions, or business-specific events.
   *
   * @param tag - Identifier for the event type (e.g., 'button-click', 'form-submit')
   * @param payload - Arbitrary data to attach to the event
   *
   * @example
   * ```typescript
   * recorder.addCustomEvent('purchase', {
   *   productId: 'SKU-123',
   *   price: 99.99,
   *   currency: 'USD'
   * });
   * ```
   */
  addCustomEvent(tag: string, payload: unknown): void {
    if (this.state !== 'recording') {
      log.warn('Cannot add custom event — recorder is not active');
      return;
    }
    try {
      this.domRecorder.addCustomEvent(tag, payload);
    } catch (err) {
      log.warn('addCustomEvent failed', err);
    }
  }

  /**
   * Optional callback fired whenever the agent moves their cursor over the
   * live session viewer. x and y are pixel coordinates in the customer's page
   * (i.e. already multiplied by window.innerWidth/Height).
   *
   * The SDK automatically renders a ghost cursor overlay — you only need this
   * if you want to do something custom in addition (e.g. highlight an element,
   * show a tooltip, etc.).
   *
   * @example
   * ```typescript
   * recorder.onAgentCursor((x, y) => {
   *   console.log('Agent is at', x, y);
   * });
   * ```
   */
  onAgentCursor(callback: (x: number, y: number) => void): () => void {
    this._cursorCallback = callback;
    return () => {
      if (this._cursorCallback === callback) this._cursorCallback = null;
    };
  }

  /**
   * Host-page override for the default consent modal. Returns an unsubscribe;
   * when no handler is registered, the SDK's built-in modal is used.
   */
  setOnControlRequest(handler: ControlRequestHandler | null): () => void {
    return this._cobrowse.setControlRequestHandler(handler);
  }

  /**
   * Returns the current status of the recorder.
   *
   * @returns RecorderStatus with sessionId and state
   *
   * @example
   * ```typescript
   * const status = recorder.getStatus();
   * // { sessionId: 'abc-123', state: 'recording' }
   * ```
   */
  getStatus(): RecorderStatus {
    return { sessionId: this.sessionId, state: this.state };
  }
}

// ---------------------------------------------------------------------------
// Public factory function (ergonomic entry point for consumers)
// ---------------------------------------------------------------------------

export function createRecorder(config: RecorderConfig): Recorder {
  return Recorder.create(config);
}
